import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';

import { DomSanitizer } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';

import {
  DataService,
  MonitorOverviewEndpointsService,
  GlobalVariables,
  MonitorOverview,
  BranchEndpointService,
} from '../common/index';
import { tokenIntvertColorFlashAnimation, FlashItem, triggerFlash, onFlashDone } from '../common/common.animation';
import { AuthButtonService } from '../auth-button/index';
import { NewTokenData, PusherService } from '../common/_services/pusher.service';

declare var swal: any;

@Component({
  selector: 'monitor-overview',
  templateUrl: './monitor_overview.component.html',
  styleUrls: ['./monitor_overview.component.scss'],
  providers: [MonitorOverviewEndpointsService],
  animations: [tokenIntvertColorFlashAnimation()],
})
export class MonitorOverviewComponent implements OnInit, OnDestroy {
  monitorOverviewData;
  counters;
  queues;
  routeId;
  sound;
  loggedIn;
  actionCable;
  animationData: { [key: string]: FlashItem } = {};
  unSubscribeCable;
  unSubscribeCable2;
  sliderId;
  iframeSrc;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private authButtonService: AuthButtonService,
    private monitorOverviewEndpointsService: MonitorOverviewEndpointsService,
    private zone: NgZone,
    private sanitizer: DomSanitizer,
    private pusherService: PusherService,
    private branchService: BranchEndpointService,
  ) {
    this.validateLogin();
    this.monitorOverviewData = new MonitorOverview();
  }

  ngOnInit() {
    if (this.loggedIn) {
      this.routeParams();
      this.loadMonitorOverview();
      this.initPusherSubscription();

      this.zone.run(() => {
        console.log('enabled page re-render');
      });
    }
  }

  ngOnDestroy() {
    this.monitorOverviewData = null;
    this.counters = null;
    this.queues = null;
    this.routeId = null;
    this.loggedIn = null;
    this.animationData = null;
    this.sliderId = null;
    this.iframeSrc = null;
  }

  initPusherSubscription() {
    const branchId = this.route.snapshot.params['branchId'];
    this.pusherService.connectTokenBroadcast(branchId, this.handleNewTokenData);
  }

  handleNewTokenData = (payload: NewTokenData) => {
    if (!payload) return;

    const { playTokenFetchSound } = this.monitorOverviewData;
    const { branchQueueId, status, tokenNumber, totalPeopleInQueue, counterName } = payload.data.attributes;

    const branchQueue = this.queues.find((it) => it.branchQueueId == branchQueueId);

    if (!branchQueue) return;

    switch (status) {
      case 'pending':
        branchQueue.peopleInQueue = totalPeopleInQueue;
        if (playTokenFetchSound && totalPeopleInQueue === 1) {
          this.branchService.playTokenEventSound(this.branchService.soundEffects.newToken);
        }
        break;

      case 'serving':
        branchQueue.peopleInQueue -= 1;
        branchQueue.currentTokenNumber = tokenNumber;
        branchQueue.counterName = counterName;
        triggerFlash(this.animationData[branchQueue.id]);
        this.branchService.playTokenEventSound(null, tokenNumber);
        break;

      default:
        break;
    }
  };

  validateLogin() {
    this.loggedIn = this.dataService.sessionData();
    if (!this.loggedIn) this.router.navigate(['/login']);
    this.authButtonService.login();
  }

  routeParams() {
    this.route.params.subscribe((params) => {
      this.routeId = +params['id']; // (+) converts string 'id' to a number
    });
  }

  initAnimationData() {
    this.animationData = {};
    this.queues.forEach((queue) => {
      this.animationData[queue.id] = { flag: false, state: 'original' };
    });
  }

  getQueueAnimationState(queue): string {
    return this.animationData[queue.id]?.state ?? 'original';
  }

  public isQueueAnimating(queue): boolean {
    return this.animationData[queue.id]?.flag ?? false;
  }

  onQueueAnimationDone(queue) {
    const data = this.animationData[queue.id];
    if (data) onFlashDone(data);
  }

  getBackground(queue) {
    return 'linear-gradient(' + queue.backgroundColor1 + ',' + queue.backgroundColor2 + ')';
  }

  loadMonitorOverview() {
    this.monitorOverviewEndpointsService.getMonitorOverviewData(this.routeId).subscribe({
      next: (data: any) => {
        this.monitorOverviewData = new MonitorOverview();
        this.monitorOverviewData.setMonitorOverviewData(data.data);
        this.queues = this.monitorOverviewData.queues;
        this.initAnimationData();
      },
      error: (error) => {
        swal({
          text: this.errorMessage(error),
          type: 'warning',
          allowOutsideClick: false,
          showCancelButton: false,
          allowEscapeKey: false,
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Refresh the page!',
        }).then(() => {
          if (error && error[0].status == '401') {
            this.dataService.clearData();
            this.router.navigate(['login']);
          } else {
            location.reload();
          }
        });
      },
    });
  }

  errorMessage(error) {
    let message;

    if (error[0] && error[0].status == '404') {
      message = GlobalVariables.ERROR_MESSAGE_404;
    } else if (error[0] && error[0].message) {
      message = error[0].message;
    } else {
      message = GlobalVariables.DEFAULT_ERROR_MESSAGE;
    }

    return message;
  }
}
